import { useEffect, useState } from "react"
import { Loader2, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { producaoApi, errorMessage, type ContractsBackend, type ContractsHostCheck, type FieldConfig, type JobKind, type ProducaoConfig } from "./api"
import { KIND_SHORT } from "./lib"
import { useProducao } from "./context"
import { selectClass } from "./AssignPanel"
import { TrelloConfig } from "./TrelloConfig"
import { confirmDialog } from "@/lib/confirm"

/**
 * Produção → Configurações · app de campo. Os campos do fecho ainda não
 * chegaram do cliente: ficam aqui, editáveis, e não no código. Qualquer pessoa
 * da Produção vê; só administradores gravam.
 */
export function Configuracoes() {
  const { shell, board } = useProducao()
  const [cfg, setCfg] = useState<FieldConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // M7: avisos da correspondência com a lista «Instalação» do contrato (modo `host`).
  const [warnings, setWarnings] = useState<string[]>([])
  const canEdit = !!shell?.isAdmin

  useEffect(() => {
    if (!shell) return
    producaoApi.fieldConfig(shell).then(setCfg).catch((err) => setError(errorMessage(err)))
  }, [shell])

  if (error) return <p className="py-24 text-center text-sm text-destructive">{error}</p>
  if (!cfg) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin" /></div>

  const set = <K extends keyof FieldConfig>(k: K, v: FieldConfig[K]) => setCfg({ ...cfg, [k]: v })
  const save = async () => {
    if (!shell) return
    if (cfg.odoo_mode === "validar" && !(await confirmDialog("Com «Validar», a app valida as transferências no Odoo (sai o documento de transporte). Confirmaste com a contabilidade?"))) return
    setSaving(true)
    try {
      const { warnings: w = [], ...saved } = await producaoApi.saveFieldConfig(shell, cfg)
      setCfg(saved)
      setWarnings(w)
      shell.bridge.toast(
        w.length
          ? { variant: "info", title: "Configuração guardada, com avisos", description: "Vê os avisos na checklist do fecho." }
          : { variant: "success", title: "Configuração guardada" },
      )
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Erro ao guardar", description: errorMessage(err) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Configurações</h1>
          <p className="text-sm text-muted-foreground">
            {canEdit ? "As alterações chegam aos telemóveis na sincronização seguinte." : "Só administradores alteram esta configuração."}
          </p>
        </div>
        {canEdit && <Button onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar</Button>}
      </header>

      <fieldset disabled={!canEdit} className="min-w-0 space-y-6">
        <Card title="Odoo">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Escritas no Odoo" htmlFor="fc-mode" hint="«Preparar» cria e preenche as transferências; o escritório valida no Odoo.">
              <select id="fc-mode" className={selectClass} value={cfg.odoo_mode} onChange={(e) => set("odoo_mode", e.target.value as FieldConfig["odoo_mode"])}>
                <option value="preparar">Preparar (não valida)</option>
                <option value="validar">Validar (sai o documento de transporte)</option>
              </select>
            </Field>
            <Field label="Localização de empréstimos/outros" htmlFor="fc-loans" hint="Nome ou caminho no Odoo, p.ex. WH/Empréstimos.">
              <Input id="fc-loans" value={cfg.loans_location ?? ""} onChange={(e) => set("loans_location", e.target.value || null)} />
            </Field>
            <Field label="Field Service (tarefa por obra)" htmlFor="fc-fsm" hint="Só com o módulo industry_fsm instalado e licenciado.">
              <label className="flex items-center gap-2 text-sm">
                <input id="fc-fsm" type="checkbox" checked={cfg.fsm_enabled} onChange={(e) => set("fsm_enabled", e.target.checked)} />
                Criar tarefa no Field Service
              </label>
            </Field>
            <Field label="Projeto do Field Service (id)" htmlFor="fc-fsm-p" hint="Vazio = o primeiro projeto de Field Service.">
              <Input id="fc-fsm-p" inputMode="numeric" value={cfg.fsm_project_id ?? ""} onChange={(e) => set("fsm_project_id", Number(e.target.value) > 0 ? Number(e.target.value) : null)} />
            </Field>
          </div>
        </Card>

        <Card title="Telemóveis">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Versão mínima da app" htmlFor="fc-min" hint="Abaixo desta, o telemóvel mostra «Atualize a aplicação».">
              <Input id="fc-min" value={cfg.min_app_version} onChange={(e) => set("min_app_version", e.target.value)} />
            </Field>
            <Field label="Duração máxima dos vídeos (segundos)" htmlFor="fc-video">
              <Input id="fc-video" type="number" min={10} max={600} value={cfg.video_max_seconds} onChange={(e) => set("video_max_seconds", Number(e.target.value))} />
            </Field>
          </div>
        </Card>

        <Card title="Checklist do fecho">
          {/* M1: os tipos por item só existem com as obras no Koortex (receitas por produto do contrato). */}
          <ChecklistEditor rows={cfg.checklist} showKinds={board?.config.contracts_backend === "host"} onChange={(rows) => set("checklist", rows)} />
          {warnings.length > 0 && (
            <ul className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" aria-live="polite">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </Card>

        <Card title="Configuração deixada ao cliente">
          <ListEditor
            rows={cfg.client_config_fields}
            empty={{ key: "", label: "", secret: false }}
            onChange={(rows) => set("client_config_fields", rows)}
            cols={[
              { key: "key", label: "Chave", width: "w-full sm:w-48", placeholder: "wifi_ssid" },
              { key: "label", label: "Texto", placeholder: "Rede Wi-Fi" },
              { key: "secret", label: "Secreto", kind: "check" },
            ]}
          />
          <p className="text-xs text-muted-foreground">Os campos secretos ficam cifrados, escondidos no G4 e fora do PDF.</p>
        </Card>

        <Card title="PDF de instruções">
          <div className="space-y-3">
            <Field label="Título" htmlFor="fc-title">
              <Input id="fc-title" value={cfg.instructions.title} onChange={(e) => set("instructions", { ...cfg.instructions, title: e.target.value })} />
            </Field>
            <Field label="Introdução" htmlFor="fc-intro">
              <TextArea id="fc-intro" value={cfg.instructions.intro} onChange={(v) => set("instructions", { ...cfg.instructions, intro: v })} />
            </Field>
            {cfg.instructions.sections.map((s, i) => (
              <div key={i} className="space-y-2 rounded-lg border p-3 border-border">
                <div className="flex gap-2">
                  <Input
                    aria-label={`Título da secção ${i + 1}`}
                    value={s.title}
                    onChange={(e) => set("instructions", { ...cfg.instructions, sections: cfg.instructions.sections.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })}
                  />
                  <Button type="button" size="icon" variant="ghost" aria-label="Apagar secção" onClick={() => set("instructions", { ...cfg.instructions, sections: cfg.instructions.sections.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <TextArea
                  id={`fc-sec-${i}`}
                  value={s.body}
                  onChange={(v) => set("instructions", { ...cfg.instructions, sections: cfg.instructions.sections.map((x, j) => (j === i ? { ...x, body: v } : x)) })}
                />
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={() => set("instructions", { ...cfg.instructions, sections: [...cfg.instructions.sections, { title: "", body: "" }] })}>
              <Plus className="h-4 w-4" /> Nova secção
            </Button>
            <Field label="Rodapé" htmlFor="fc-footer">
              <Input id="fc-footer" value={cfg.instructions.footer} onChange={(e) => set("instructions", { ...cfg.instructions, footer: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={cfg.send_instructions_email} onChange={(e) => set("send_instructions_email", e.target.checked)} />
              Enviar o PDF por email ao cliente no fecho
            </label>
          </div>
        </Card>
      </fieldset>

      <TrelloConfig />
      <ContractsBackendCard />
    </div>
  )
}

/**
 * Onde vive o estado das obras durante a transição para os contratos do
 * Koortex: `local` (como sempre) ou `host` (a obra é um contrato). Só
 * administradores mudam; muda tudo de uma vez.
 */
function ContractsBackendCard() {
  const { shell, reload } = useProducao()
  const canEdit = !!shell?.isAdmin
  const [cfg, setCfg] = useState<ProducaoConfig | null>(null)
  const [saving, setSaving] = useState(false)
  // Passar para os contratos: a verificação do Koortex antes de gravar.
  const [check, setCheck] = useState<ContractsHostCheck | "loading" | null>(null)

  useEffect(() => {
    if (!shell) return
    producaoApi.config(shell).then(setCfg).catch(() => undefined)
  }, [shell])
  if (!cfg) return null

  const save = async (value: ContractsBackend) => {
    if (!shell) return
    setSaving(true)
    try {
      const { warnings = [], ...saved } = await producaoApi.saveConfig(shell, { contracts_backend: value })
      setCfg(saved)
      setCheck(null)
      await reload()
      shell.bridge.toast({
        variant: warnings.length ? "info" : "success",
        title: value === "host" ? "Obras sobre os contratos do Koortex" : "Obras geridas na app",
        ...(warnings.length ? { description: warnings.join(" ") } : {}),
      })
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Erro ao guardar", description: errorMessage(err) })
    } finally {
      setSaving(false)
    }
  }

  const change = async (value: ContractsBackend) => {
    if (!shell || value === cfg.contracts_backend) return
    if (value === "local") {
      if (await confirmDialog("Voltar a gerir as obras na app? Deixam de escrever nos contratos do Koortex (o que já lá está fica).")) await save("local")
      return
    }
    setCheck("loading")
    try {
      setCheck(await producaoApi.contractsCheck(shell))
    } catch (err) {
      setCheck(null)
      shell.bridge.toast({ variant: "error", title: "Não foi possível verificar o Koortex", description: errorMessage(err) })
    }
  }

  return (
    <Card title="Contratos do Koortex">
      <Field
        label="Obras"
        htmlFor="cb-mode"
        hint="«Sobre os contratos do Koortex»: cada obra é um contrato (fases, adjudicação, pagamentos 30/60/10, aceitação)."
      >
        <select
          id="cb-mode"
          className={selectClass}
          value={cfg.contracts_backend}
          disabled={!canEdit || saving || check === "loading"}
          onChange={(e) => void change(e.target.value as ContractsBackend)}
        >
          <option value="local">Geridas na app</option>
          <option value="host">Sobre os contratos do Koortex</option>
        </select>
      </Field>
      {check === "loading" && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> A verificar o Koortex…</p>
      )}
      {check && check !== "loading" && (
        <div className="space-y-2 rounded-md border p-3 text-sm border-border" aria-live="polite">
          <p className="font-medium">Verificação do Koortex</p>
          <ul className="space-y-0.5 text-xs">
            <li>{check.options_ok ? "✓" : "✗"} O Koortex responde aos contratos</li>
            <li>{check.has_type ? "✓" : "✗"} Tipo de contrato «Instalação» (bundle maisenergia-contratos)</li>
            <li>{check.has_payment_terms ? "✓" : "!"} Condição de pagamento 30/60/10</li>
            <li>{check.hold_reasons > 0 ? "✓" : "!"} Motivos de paragem ({check.hold_reasons})</li>
            <li>{check.vans_without_team.length ? "!" : "✓"} Carrinhas com equipa do Koortex{check.vans_without_team.length ? ` — faltam ${check.vans_without_team.length}` : ""}</li>
          </ul>
          {check.error && <p className="text-sm text-destructive">{check.error}</p>}
          {check.warnings.length > 0 && (
            <ul className="space-y-1 text-xs text-amber-800">{check.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setCheck(null)} disabled={saving}>Cancelar</Button>
            <Button size="sm" onClick={() => void save("host")} disabled={!check.ok || saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Passar para os contratos
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border p-4 border-border">
      <h2 className="text-sm font-semibold">{title}</h2>
      {children}
    </section>
  )
}

function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function TextArea({ id, value, onChange }: { id: string; value: string; onChange(v: string): void }) {
  return (
    <textarea
      id={id}
      rows={3}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm"
    />
  )
}

type ChecklistRow = FieldConfig["checklist"][number]

/**
 * Checklist do fecho: chave, texto e os tipos de obra a que o item se aplica
 * (decisão 6 — nenhum marcado = todos). Os itens das receitas por produto do
 * contrato entram sozinhos ao gravar (modo `host`), já com o tipo.
 */
function ChecklistEditor({ rows, showKinds, onChange }: { rows: ChecklistRow[]; showKinds: boolean; onChange(rows: ChecklistRow[]): void }) {
  const update = (i: number, patch: Partial<ChecklistRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const toggleKind = (i: number, kind: JobKind, on: boolean) => {
    const cur = rows[i]!.kinds ?? []
    const next = on ? [...new Set([...cur, kind])] : cur.filter((k) => k !== kind)
    update(i, { kinds: next })
  }
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="space-y-1.5 border-b pb-2 last:border-0 border-border">
          <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            <Input aria-label="Chave" placeholder="sistema_ligado" className="w-full sm:w-48" value={r.id} onChange={(e) => update(i, { id: e.target.value })} />
            <Input aria-label="Texto" placeholder="Sistema ligado e a funcionar" className="min-w-0 flex-1" value={r.label} onChange={(e) => update(i, { label: e.target.value })} />
            <Button type="button" size="icon" variant="ghost" aria-label="Apagar" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {showKinds && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>Tipos{r.kinds?.length ? "" : " (todos)"}:</span>
            {JOB_KIND_KEYS.map((k) => (
              <label key={k} className="flex items-center gap-1">
                <input type="checkbox" checked={r.kinds?.includes(k) ?? false} onChange={(e) => toggleKind(i, k, e.target.checked)} />
                {KIND_SHORT[k]}
              </label>
            ))}
          </div>
          )}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...rows, { id: "", label: "" }])}>
        <Plus className="h-4 w-4" /> Acrescentar
      </Button>
    </div>
  )
}

const JOB_KIND_KEYS: JobKind[] = ["solar", "ac", "bc_aqs", "bc_aq"]

type Col = { key: string; label: string; width?: string; placeholder?: string; kind?: "text" | "check" }

function ListEditor<T extends Record<string, string | boolean | null | undefined>>({
  rows,
  empty,
  cols,
  onChange,
}: {
  rows: T[]
  empty: T
  cols: Col[]
  onChange(rows: T[]): void
}) {
  const update = (i: number, key: string, value: string | boolean) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)))
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 border-b pb-2 last:border-0 border-border sm:flex-nowrap sm:border-0 sm:pb-0">
          {cols.map((c) =>
            c.kind === "check" ? (
              <label key={c.key} className="flex shrink-0 items-center gap-1 text-xs">
                <input type="checkbox" checked={r[c.key] === true} onChange={(e) => update(i, c.key, e.target.checked)} />
                {c.label}
              </label>
            ) : (
              <Input
                key={c.key}
                aria-label={c.label}
                placeholder={c.placeholder}
                className={c.width ?? "min-w-0 flex-1"}
                value={String(r[c.key] ?? "")}
                onChange={(e) => update(i, c.key, e.target.value)}
              />
            ),
          )}
          <Button type="button" size="icon" variant="ghost" aria-label="Apagar" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...rows, { ...empty }])}>
        <Plus className="h-4 w-4" /> Acrescentar
      </Button>
    </div>
  )
}
